import { useQueryClient } from '@tanstack/react-query'
import { Download, Home, RefreshCw, Settings } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { useMonetizationSummary } from '../hooks/useMonetizationSummary'
import type { MonetizationFilters } from '../types/monetization.types'
import { AgentPerformanceGrid } from './AgentPerformanceGrid'
import { AnalyticsGrid } from './AnalyticsGrid'
import { KPIMetricsHeader } from './KPIMetricsHeader'

export function MonetizationDashboard() {
  const { setBreadcrumbs } = useBreadcrumbs()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<MonetizationFilters>({
    dateRange: '30d',
    agentStatus: 'all',
    performance: 'all',
    sortBy: 'revenue',
    sortOrder: 'desc',
  })

  // Compute date range from filter
  const { fromDate, toDate } = useMemo(() => {
    const to = new Date()
    const days =
      filters.dateRange === '7d' ? 7 : filters.dateRange === '30d' ? 30 : 90
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    return {
      fromDate: from.toISOString(),
      toDate: to.toISOString(),
    }
  }, [filters.dateRange])

  const { data, isLoading, isFetching } = useMonetizationSummary(
    fromDate,
    toDate
  )

  // Set up breadcrumbs
  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  // Filter and sort agents
  const filteredAgents = useMemo(() => {
    if (!data) {
      return []
    }
    let agents = [...data.agents]

    if (filters.agentStatus !== 'all') {
      agents = agents.filter(a => a.status === filters.agentStatus)
    }

    agents.sort((a, b) => {
      const mult = filters.sortOrder === 'asc' ? 1 : -1
      switch (filters.sortBy) {
        case 'revenue':
          return (a.revenue_usd - b.revenue_usd) * mult
        case 'margin':
          return (a.margin_percentage - b.margin_percentage) * mult
        case 'name':
          return a.agent_name.localeCompare(b.agent_name) * mult
        default:
          return 0
      }
    })

    return agents
  }, [data, filters])

  // Map time series for charts
  const chartData = useMemo(
    () =>
      (data?.time_series ?? []).map(ts => ({
        date: ts.date,
        revenue: ts.revenue_usd,
        cost: ts.cost_usd,
        margin: ts.margin_usd,
        tasks: ts.task_runs,
        workflows: ts.workflow_runs,
        outcomes: ts.outcome_runs,
      })),
    [data]
  )

  // Margin by agent for bar chart
  const marginByAgent = useMemo(
    () =>
      (data?.agents ?? []).map(a => ({
        agentName: a.agent_name,
        revenue: a.revenue_usd,
        cost: a.cost_usd,
        margin: a.margin_usd,
      })),
    [data]
  )

  // Revenue by agent for pie chart (no "Others" grouping)
  const revenueByAgent = useMemo(() => {
    const agents = data?.agents ?? []
    const total = agents.reduce((sum, a) => sum + a.revenue_usd, 0)
    return agents.map(a => ({
      agentName: a.agent_name,
      value: a.revenue_usd,
      percentage: total > 0 ? (a.revenue_usd / total) * 100 : 0,
    }))
  }, [data])

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['monetization-summary'] })
  }

  const handleExport = () => {
    // TODO: Export dashboard data
  }

  const handleViewAgent = (_agentId: string) => {
    // TODO: Navigate to agent details
  }

  // Use a stable empty summary for the skeleton state
  const emptySummary = {
    total_agents: 0,
    total_task_runs: 0,
    total_workflow_runs: 0,
    total_outcome_runs: 0,
    total_revenue_usd: 0,
    total_cost_usd: 0,
    total_margin_usd: 0,
  }

  return (
    <div className='space-y-8'>
      {/* Header Section */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Agent Monetization Dashboard'
            subtitle='Monitor performance, revenue, and cost optimization across all your AI agents'
          />
        </div>

        <div className='flex gap-3 items-center w-full md:w-auto'>
          <div className='flex rounded-lg border'>
            {(['7d', '30d', '90d'] as const).map(period => (
              <Button
                key={period}
                variant={filters.dateRange === period ? 'default' : 'ghost'}
                size='sm'
                onClick={() =>
                  setFilters(prev => ({ ...prev, dateRange: period }))
                }
                className='rounded-none first:rounded-l-lg last:rounded-r-lg'
              >
                {period}
              </Button>
            ))}
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant='outline' size='sm' onClick={handleExport}>
            <Download className='w-4 h-4 mr-2' />
            Export
          </Button>
          <Button variant='outline' size='sm'>
            <Settings className='w-4 h-4 mr-2' />
            Configure
          </Button>
        </div>
      </div>

      {/* Key Metrics Header */}
      <KPIMetricsHeader
        summary={data?.summary ?? emptySummary}
        loading={isLoading}
      />

      {/* Agent Performance Grid */}
      <AgentPerformanceGrid
        agents={filteredAgents}
        loading={isLoading}
        onViewAgent={handleViewAgent}
      />

      {/* Analytics Charts */}
      <AnalyticsGrid
        chartData={chartData}
        marginByAgent={marginByAgent}
        revenueByAgent={revenueByAgent}
        loading={isLoading}
      />
    </div>
  )
}
